import React, { useCallback } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { Header } from '../landing/Header';
import { Footer } from '../landing/Footer';
import { Container } from '../landing/Container';

const EMBED_SNIPPET = `<!-- SmilesFlow Embed -->
<div data-sf-embed>
  <iframe
    src="https://www.smilesflow.com/p/YOUR_CAMPAIGN_ID?utm_source=embed"
    style="position:absolute;top:0;left:0;width:100%;height:100%;border:none;"
    title="Play our branded game"
    loading="lazy"
    allow="web-share; fullscreen; autoplay"
    sandbox="allow-scripts allow-same-origin allow-forms allow-popups"
  ></iframe>
</div>
<style>
  [data-sf-embed] {
    position: relative;
    width: 100%;
    max-width: 420px;
    aspect-ratio: 9 / 16;
    max-height: 70vh;
    margin: 0 auto;
    border-radius: 16px;
    overflow: hidden;
    box-shadow: 0 10px 30px rgba(0,0,0,0.15);
    background: #1a2530;
  }
  @media (min-width: 768px) {
    [data-sf-embed] {
      max-width: 900px;
      aspect-ratio: 16 / 9;
      max-height: none;
    }
  }
</style>`;

const BLOG_BG = '#354858';

function CopyButton({ text }) {
  const [copied, setCopied] = React.useState(false);
  const handleCopy = useCallback(() => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }, [text]);
  return (
    <button
      onClick={handleCopy}
      className="absolute top-3 right-3 px-3 py-1.5 rounded-lg bg-white/10 hover:bg-white/20 text-xs font-medium text-white/70 hover:text-white transition-all"
    >
      {copied ? 'Copied!' : 'Copy'}
    </button>
  );
}

function GameEmbed({ campaignId, title = 'Interactive Game by SmilesFlow' }) {
  const src = `${window.location.origin}/p/${campaignId}?utm_source=embed`;
  return (
    <>
      <style>{`
        [data-sf-embed]{position:relative;width:100%;max-width:420px;aspect-ratio:9/16;max-height:70vh;margin:0 auto;border-radius:16px;overflow:hidden;box-shadow:0 10px 30px rgba(0,0,0,0.15);background:#1a2530}
        @media(min-width:768px){[data-sf-embed]{max-width:900px;aspect-ratio:16/9;max-height:none}}
      `}</style>
      <div data-sf-embed>
        <iframe
          src={src}
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            border: 'none',
          }}
          title={title}
          loading="lazy"
          allow="web-share; fullscreen; autoplay"
          sandbox="allow-scripts allow-same-origin allow-forms allow-popups"
        />
      </div>
    </>
  );
}

export default function BlogExample() {
  React.useEffect(() => {
    document.body.style.backgroundColor = BLOG_BG;
    return () => { document.body.style.backgroundColor = ''; };
  }, []);

  // Example campaign ID — replace with a real published campaign
  const exampleCampaignId = '9zndRwCUkwUnZnXqV5Yt';

  return (
    <div className="landing-page min-h-screen flex flex-col bg-gch-bg text-white antialiased scroll-smooth pt-[calc(5rem+env(safe-area-inset-top))]">
      <Helmet>
        <title>How to Embed a Branded Mini-Game on Your Website | SmilesFlow</title>
        <meta name="description" content="Learn how to add an interactive branded mini-game to your website or blog in under 2 minutes. Copy one snippet, paste it in, done." />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://www.smilesflow.com/blog/embed-a-game" />
        {/* Open Graph */}
        <meta property="og:type" content="article" />
        <meta property="og:title" content="How to Embed a Branded Mini-Game on Your Website" />
        <meta property="og:description" content="One snippet. Any website. Drop a playable branded arcade game into your blog, landing page, or product page in under 2 minutes." />
        <meta property="og:url" content="https://www.smilesflow.com/blog/embed-a-game" />
        <meta property="og:site_name" content="SmilesFlow" />
        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="How to Embed a Branded Mini-Game on Your Website" />
        <meta name="twitter:description" content="One snippet. Any website. Drop a playable branded arcade game into your blog, landing page, or product page in under 2 minutes." />
      </Helmet>

      <Header />

      <main className="flex-1">
        {/* Article header */}
        <section className="py-16 sm:py-20">
          <Container className="max-w-3xl">
            <Link to="/#integrations" className="text-sm text-gch-accent hover:underline mb-6 inline-block">&larr; Back to SmilesFlow</Link>
            <p className="text-sm font-medium text-gch-accent uppercase tracking-wider mb-3">Guide</p>
            <h1 className="text-3xl sm:text-4xl font-bold tracking-tight leading-tight">
              How to Embed a Branded Mini-Game on Your Website
            </h1>
            <p className="mt-4 text-lg text-white/70 leading-relaxed max-w-2xl">
              Drop a playable, branded arcade game into any webpage: WordPress, Webflow, Squarespace, Notion, or plain HTML. One embed snippet, zero dependencies.
            </p>
            <div className="mt-4 flex items-center gap-3 text-sm text-white/50">
              <span>5 min read</span>
              <span className="w-1 h-1 rounded-full bg-white/30" />
              <span>Updated March 2026</span>
            </div>
          </Container>
        </section>

        {/* Article body */}
        <section className="pb-20">
          <Container className="max-w-3xl">
            <article className="space-y-6 [&_h2]:text-gch-accent [&_h2]:text-2xl [&_h2]:font-bold [&_h2]:mt-12 [&_h2]:mb-4 [&_p]:text-white/80 [&_p]:leading-relaxed [&_p]:text-lg [&_li]:text-white/80 [&_li]:text-lg [&_li]:leading-relaxed [&_code]:bg-white/10 [&_code]:px-1.5 [&_code]:py-0.5 [&_code]:rounded [&_code]:text-sm [&_code]:text-gch-accent [&_pre]:bg-gch-surface [&_pre]:rounded-xl [&_pre]:p-5 [&_pre]:overflow-x-auto [&_pre]:text-sm [&_pre_code]:bg-transparent [&_pre_code]:p-0 [&_pre_code]:text-white/70 [&_a]:text-gch-accent [&_a]:underline [&_ol]:list-decimal [&_ol]:pl-6 [&_ol]:space-y-2 [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:space-y-2">

              <h2>Why embed a game?</h2>
              <p>
                Blog posts with interactive content see significantly higher time-on-page and engagement.
                A branded arcade game gives visitors something to <em>do</em>, and when they compete on the
                leaderboard, they opt into your list.
              </p>
              <ul>
                <li>No app download required. Plays instantly in the browser.</li>
                <li>Works on mobile and desktop</li>
                <li>Leaderboard drives repeat visits and sharing</li>
                <li>Captures leads through optional email entry</li>
              </ul>

              <h2>Try it. Play right here.</h2>
              <p>
                This is a live SmilesFlow game running inside this page. Go on, have a go:
              </p>

              <div className="my-10">
                <GameEmbed campaignId={exampleCampaignId} />
              </div>

              <p className="text-center !text-white/50 !text-sm">
                Want one of these on your site?{' '}
                <Link to="/studio/register" className="text-gch-accent hover:text-white transition-colors">
                  Create yours free
                </Link>{' '}
                . Embedding works on every plan, including free.
              </p>

              <h2>How to add it to your site</h2>
              <p>
                Once you've published a campaign in the SmilesFlow studio, grab the embed code
                from your campaign's share settings. It looks like this:
              </p>

              <div className="relative">
                <CopyButton text={EMBED_SNIPPET} />
                <pre>
                  <code>{EMBED_SNIPPET}</code>
                </pre>
              </div>

              <p>
                Replace <code>YOUR_CAMPAIGN_ID</code> with your campaign's ID. Paste the snippet anywhere
                in your HTML: blog post, landing page, Webflow embed block, Notion site, WordPress custom HTML widget.
              </p>

              <h2>Customisation tips</h2>
              <ol>
                <li><strong>Responsive by default.</strong> The embed switches between portrait (9:16) on mobile and landscape (16:9) on desktop automatically. No extra work needed.</li>
                <li><strong>Max width.</strong> Adjust the <code>max-width</code> values in the style block to fit your layout. Desktop defaults to 900px; mobile to 420px.</li>
                <li><strong>Dark or light page?</strong> Change the <code>background</code> colour to match your site so the loading state blends in.</li>
                <li><strong>Tracking.</strong> Add <code>?utm_source=blog&amp;utm_campaign=launch</code> to the iframe URL to separate embed plays from direct link plays in your analytics.</li>
              </ol>

              <h2>What happens when someone plays?</h2>
              <p>
                Every play, leaderboard join, CTA click, and email capture shows up in your
                SmilesFlow analytics dashboard, with or without the embed. The game works exactly
                the same as a direct link or QR code, just hosted inside your page.
              </p>

              <div className="mt-12 rounded-2xl bg-gch-surface border border-white/10 p-8 text-center">
                <p className="text-lg font-semibold !text-white mb-2">Ready to create your own?</p>
                <p className="!text-white/60 !text-base mb-6">Build a branded game in minutes. Free to build, £39 to publish.</p>
                <Link
                  to="/studio/register"
                  className="inline-flex items-center justify-center h-11 rounded-xl bg-gradient-to-b from-gch-cta to-[#E05848] px-6 text-sm font-semibold text-white shadow-lg hover:shadow-xl transition-all no-underline border border-gch-accent/30"
                >
                  Get started free
                </Link>
              </div>
            </article>
          </Container>
        </section>
      </main>

      <Footer />
    </div>
  );
}
